var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box } from "@gemini/core";
import { InfoAgencyContactPicto } from "@gemini/illustrations";
import { Modal, useSnackbar } from "@gemini/ui";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm, useFormContext, useWatch, } from "react-hook-form";
import { useNormalizeNameFields } from "@/components/contacting/hooks/useNormalizeNameFields";
import { useIsConfirmationPageDisabled } from "@/components/contacting/hooks/useIsConfirmationPageDisabled";
import { useClassifiedId } from "@/contexts/classified";
import { useConfirmationPage } from "@/contexts/confirmPage";
import { useContactContext, useSchema } from "@/contexts/contact";
import { useContactingTestId, useSendMultipleLeads } from "@/hooks";
import { useI18n } from "@/shared/hooks";
import { MultiInquirySimilarListings } from "./MultiInquirySimilarListings";
export const MultiInquiryModal = () => {
    var _a, _b;
    const { t } = useI18n();
    const { add: addSnackbar } = useSnackbar();
    const testId = useContactingTestId("MultiInquiryModal");
    const { getValues, formState } = useFormContext();
    const { toggleMultiInquiryModal, closeMultiInquiryModal, isMultiInquiryModalOpen, userData: loggedInUserData, userCookieData, similarListings, isSimilarListingsFetched, } = useContactContext();
    const { triggerConfirmView } = useConfirmationPage();
    const { sendMultipleLeads, loading } = useSendMultipleLeads();
    const classifiedId = useClassifiedId();
    const isConfirmationPageDisabled = useIsConfirmationPageDisabled();
    const normalizeNameFields = useNormalizeNameFields();
    const defaultMessage = (_b = (_a = formState === null || formState === void 0 ? void 0 : formState.defaultValues) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : "";
    const schema = useSchema().shape;
    const requiredFields = Object.entries(schema)
        .filter(([, field]) => !field.isOptional())
        .map(([key]) => key);
    const isPresent = (v) => v !== null && v !== undefined && (typeof v !== "string" || v.trim() !== "");
    // Get combined data from form and user
    const combineData = (formData, loggedInUserData, userCookieData) => {
        var _a, _b;
        const keys = new Set([
            ...Object.keys(formData !== null && formData !== void 0 ? formData : {}),
            ...Object.keys(loggedInUserData !== null && loggedInUserData !== void 0 ? loggedInUserData : {}),
            ...Object.keys(userCookieData !== null && userCookieData !== void 0 ? userCookieData : {}),
        ]);
        const result = {};
        for (const key of keys) {
            if (formData && key in formData) {
                const isRequired = requiredFields.includes(key);
                const formValue = formData[key];
                if (isRequired &&
                    (formValue === "" || formValue === null || formValue === undefined)) {
                    result[key] =
                        (_a = [loggedInUserData === null || loggedInUserData === void 0 ? void 0 : loggedInUserData[key], userCookieData === null || userCookieData === void 0 ? void 0 : userCookieData[key]].find(isPresent)) !== null && _a !== void 0 ? _a : "";
                }
                else {
                    result[key] = formValue !== null && formValue !== void 0 ? formValue : "";
                }
            }
            else {
                result[key] =
                    (_b = [loggedInUserData === null || loggedInUserData === void 0 ? void 0 : loggedInUserData[key], userCookieData === null || userCookieData === void 0 ? void 0 : userCookieData[key]].find(isPresent)) !== null && _b !== void 0 ? _b : "";
            }
        }
        return result;
    };
    const formData = getValues();
    const hasAddress = [loggedInUserData, userCookieData].some((u) => (u === null || u === void 0 ? void 0 : u.street) && (u === null || u === void 0 ? void 0 : u.city) && (u === null || u === void 0 ? void 0 : u.zipCode));
    const combinedFormAndUserData = combineData(formData, loggedInUserData, userCookieData);
    const haveAllRequiredLeadData = requiredFields.every((field) => {
        const value = combinedFormAndUserData[field];
        if (typeof value === "boolean") {
            return true; // always accept boolean, even if false
        }
        return Boolean(value);
    });
    // Only include listings with extended form if user has all necessary location data
    const filteredListings = useMemo(() => (similarListings || []).filter((listing) => {
        var _a;
        const enabled = (_a = listing.rawData) === null || _a === void 0 ? void 0 : _a.contactLocationEnabled;
        return !enabled || (enabled && hasAddress);
    }), [similarListings, hasAddress]);
    const methods = useForm();
    const watched = useWatch({ control: methods.control });
    const selectedListingIds = Object.keys(watched).filter((id) => watched[id]);
    const selectedListingsCount = selectedListingIds.length;
    const selectedListings = filteredListings.filter((listing) => selectedListingIds.includes(listing.id));
    useEffect(() => {
        if (!classifiedId)
            return;
        // Missing required data flow
        if (!haveAllRequiredLeadData) {
            if (!isMultiInquiryModalOpen)
                return;
            // If we dont have all required data for the lead and modal is opened,
            // go to confirmation page or show snackbar.
            if (isConfirmationPageDisabled) {
                closeMultiInquiryModal();
                addSnackbar({
                    variant: "info",
                    description: t("cdpSeeker_contactForm_success_message"),
                });
            }
            else {
                triggerConfirmView("form");
            }
        }
    }, [
        classifiedId,
        haveAllRequiredLeadData,
        isMultiInquiryModalOpen,
        isConfirmationPageDisabled,
        triggerConfirmView,
        closeMultiInquiryModal,
        addSnackbar,
        t,
    ]);
    // If there are no applicable listings, skip showing modal entirely
    // and go to confirmation page or show snackbar
    useEffect(() => {
        if (isSimilarListingsFetched &&
            isMultiInquiryModalOpen &&
            filteredListings.length === 0) {
            if (isConfirmationPageDisabled) {
                closeMultiInquiryModal();
                addSnackbar({
                    variant: "info",
                    description: t("cdpSeeker_contactForm_success_message"),
                });
            }
            else
                triggerConfirmView("form");
        }
    }, [
        isSimilarListingsFetched,
        filteredListings,
        isMultiInquiryModalOpen,
        isConfirmationPageDisabled,
        triggerConfirmView,
        closeMultiInquiryModal,
        addSnackbar,
        t,
    ]);
    const handleSubmitAndClose = () => __awaiter(void 0, void 0, void 0, function* () {
        const av_zone = "multi_inquiry";
        // Force the default message, ignoring any user edits in the UI
        const payloadFormData = Object.assign(Object.assign({}, combinedFormAndUserData), { message: defaultMessage });
        const normalized = normalizeNameFields(payloadFormData);
        yield sendMultipleLeads({
            formData: normalized,
            selectedListingIds,
            selectedListings,
            av_zone,
        });
    });
    // Only show modal when we have listings to show
    const shouldShowModal = isMultiInquiryModalOpen &&
        filteredListings.length > 0 &&
        haveAllRequiredLeadData;
    return (_jsx(FormProvider, Object.assign({}, methods, { children: _jsx(Modal, { testId: testId, trigger: () => null, isDismissable: true, isOpen: shouldShowModal, onOpenChange: () => {
                toggleMultiInquiryModal();
            }, footerStartButtonProps: {
                onPress: () => {
                    if (isConfirmationPageDisabled) {
                        return closeMultiInquiryModal();
                    }
                    return triggerConfirmView("form");
                },
                children: t("contact.multi_inquiry.skip"),
            }, footerEndButtonProps: {
                onPress: () => {
                    handleSubmitAndClose();
                },
                children: t("contact.multi_inquiry.send_requests", {
                    replace: {
                        value: selectedListingsCount,
                    },
                }),
                isDisabled: !selectedListingsCount || loading,
                isLoading: loading,
            }, children: _jsxs(Box, { width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "spacing.24", children: [_jsxs(Box, { width: "100%", height: {
                            "breakpoint.base": "fit-content",
                            "breakpoint.lg": "sizing.80",
                        }, display: "flex", flexDirection: {
                            "breakpoint.base": "column",
                            "breakpoint.lg": "row",
                        }, justifyContent: {
                            "breakpoint.base": "center",
                            "breakpoint.lg": "flex-start",
                        }, alignItems: {
                            "breakpoint.base": "center",
                            "breakpoint.lg": "flex-start",
                        }, gap: {
                            "breakpoint.base": "spacing.12",
                            "breakpoint.lg": "spacing.24",
                        }, children: [_jsx(Box, { width: "sizing.72", height: "sizing.72", children: _jsx(InfoAgencyContactPicto, { size: "80", "aria-label": "Agency contact info" }) }), _jsxs(Box, { display: "flex", flexDirection: "column", alignItems: {
                                    "breakpoint.base": "center",
                                    "breakpoint.lg": "start",
                                }, gap: "spacing.4", children: [_jsx(Box, { typography: {
                                            "breakpoint.base": "typography.headline.20.bold",
                                            "breakpoint.lg": "typography.headline.22.bold",
                                        }, textAlign: {
                                            "breakpoint.base": "center",
                                            "breakpoint.lg": "left",
                                        }, children: t("contact.multi_inquiry.title") }), _jsx(Box, { typography: "typography.body.16.regular", textAlign: {
                                            "breakpoint.base": "center",
                                            "breakpoint.lg": "left",
                                        }, children: t("contact.multi_inquiry.subtitle") })] })] }), _jsx(MultiInquirySimilarListings, { similarListings: filteredListings })] }) }) })));
};
